import { numberUtil } from '@xtnd/web/shared/utils';
import { SummaryCardsSkeleton, SummaryCardsGrid, type SummaryCardProps } from '../../../components';
import { useTrustSummary } from '../../../queries/trustReconciliationQuery';
import { ErrorCard } from '@xtnd/web/shared/components';
import { parseApiErrorUtil } from '@xtnd/web/shared/utils/parseApiErrorUtil';

export function TrustReconciliationSummary() {
  const { data, isLoading, error, refetch } = useTrustSummary();
  if (isLoading) {
    return (
      <SummaryCardsSkeleton
        count={4}
        columns={{ mobile: 1, tablet: 2, desktop: 4 }}
        gap="lg"
        useSkeletonComponent={true}
      />
    );
  }

  if (error) {
    return (
      <ErrorCard
        title="Error Loading Trust Reconciliation Summary"
        message={error instanceof Error ? error.message : 'An unexpected error occurred'}
        onRetry={() => refetch()}
        statusCode={parseApiErrorUtil(error)?.statusCode}
      />
    );
  }

  if (!data) {
    return null;
  }

  const summaryCards: Omit<SummaryCardProps, 'className'>[] = [
    {
      title: 'Total Transactions',
      value: numberUtil.formatNumber(data.totalTransactions),
      subtitle: `${numberUtil.formatNumber(data.totalClioTransactions || 0)} from Clio, ${numberUtil.formatNumber(data.totalQboTransactions || 0)} from QBO`,
      icon: 'HiClipboardList',
      color: 'blue',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'lg',
      cardBodyClassName: 'p-4',
      tooltip:
        'Total number of reconciliation records. Each record represents either a matched triplet (Clio + QBO Bank + QBO Liability) or a transaction missing in one or more systems. The Clio and QBO counts show how many transactions from each source are included in these records.',
    },
    {
      title: 'Match Success Rate',
      value: numberUtil.formatPercentage(data.matchSuccessRate, 1, false),
      subtitle: `${numberUtil.formatNumber(data.fullyMatched)} of ${numberUtil.formatNumber(data.totalTransactions)} fully matched`,
      icon: 'HiCheckCircle',
      color: 'green',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'lg',
      cardBodyClassName: 'p-4',
      tooltip:
        'Percentage of transactions that are fully matched across all three sources: Clio, QBO Bank, and QBO Liability.',
    },
    {
      title: 'Missing in QBO',
      value: numberUtil.formatNumber(
        (data.missingInQboBank || 0) +
          (data.missingInQboLiability || 0) +
          (data.missingInBothQbo || 0)
      ),
      subtitle: `${numberUtil.formatNumber(data.missingInQboBank || 0)} Bank only, ${numberUtil.formatNumber(data.missingInQboLiability || 0)} Liability only, ${numberUtil.formatNumber(data.missingInBothQbo || 0)} Both`,
      icon: 'HiExclamationCircle',
      color: 'yellow',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'lg',
      cardBodyClassName: 'p-4',
      tooltip:
        'Transactions that exist in Clio but are missing in QuickBooks. This includes transactions missing in QBO Bank account only, QBO Liability account only, or both accounts.',
    },
    {
      title: 'Missing in Clio',
      value: numberUtil.formatNumber(data.missingInClio || 0),
      subtitle: '',
      icon: 'HiExclamationCircle',
      color: 'purple',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'lg',
      cardBodyClassName: 'p-4',
      tooltip:
        'Transactions that exist in QuickBooks (either in the Bank or Liability account) but are missing in Clio. These transactions need to be reviewed and either added to Clio or removed from QuickBooks.',
    },
  ];

  const amountCards: Omit<SummaryCardProps, 'className'>[] = [
    {
      title: 'Clio Trust Amount',
      value: numberUtil.formatCurrency(data.totalClioAmount),
      subtitle: 'Total from Clio transactions',
      icon: 'HiDatabase',
      color: 'blue',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'md',
      cardBodyClassName: 'p-4',
    },
    {
      title: 'QBO Bank Amount',
      value: numberUtil.formatCurrency(data.totalQboBankAmount),
      subtitle: 'Total in Trust Bank account',
      icon: 'HiCash',
      color: 'green',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'md',
      cardBodyClassName: 'p-4',
    },
    {
      title: 'QBO Liability Amount',
      value: numberUtil.formatCurrency(data.totalQboLiabilityAmount),
      subtitle: 'Total in Trust Liability account',
      icon: 'HiDocumentText',
      color: 'purple',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'md',
      cardBodyClassName: 'p-4',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Main Summary Cards */}
      <SummaryCardsGrid
        cards={summaryCards.map(card => ({
          ...card,
          className: '',
        }))}
        columns={{ mobile: 1, tablet: 2, desktop: 4 }}
        gap="lg"
      />

      {/* 3-Way Amount Comparison */}
      <SummaryCardsGrid
        cards={amountCards.map(card => ({
          ...card,
          className: '',
        }))}
        columns={{ mobile: 1, tablet: 3, desktop: 3 }}
        gap="lg"
      />
    </div>
  );
}
